import { supabase, getUserProfile, updateUserProfile, getUserAchievements, unlockAchievement } from './supabaseClient';
import { getUserProfile as getLocalUserProfile, saveUserProfile as saveLocalUserProfile, UserProfile as LocalUserProfile } from '../utils/localUserProfile';
import {
  getUserAchievementsLocal,
  saveUserAchievementLocal,
  getAllProgress,
  getUnsyncedReadingSessions,
  markReadingSessionSynced,
  getUnsyncedDailyChallenges,
  markDailyChallengeSynced,
} from '../utils/indexedDB';
import { MembershipTier, UserBookProgress, UserChapterProgress } from '../types/membership';

// Sync status types
export type SyncStatus = 'synced' | 'pending' | 'offline' | 'syncing' | 'error';

// Last sync timestamp storage
const LAST_SYNC_KEY = 'flashread_last_sync';

/**
 * Check if user is Ultra tier and sync is enabled
 */
async function canSync(userTier: MembershipTier): Promise<boolean> {
  return userTier === 'ultra' && !!supabase;
}

/**
 * Get last sync timestamp
 */
function getLastSyncTime(): string | null {
  return localStorage.getItem(LAST_SYNC_KEY);
}

/**
 * Update last sync timestamp
 */
function updateLastSyncTime() {
  localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString());
}

/**
 * Sync local user profile to Supabase (Ultra only)
 */
async function syncUserProfile(userId: string, userTier: MembershipTier): Promise<boolean> {
  if (userTier !== 'ultra') return false;

  try {
    // Get local profile
    const localProfile = await getLocalUserProfile(userId);
    if (!localProfile) return false;

    // Get remote profile
    const remoteProfile = await getUserProfile(userId);
    
    // Conflict resolution: last-write-wins
    const localTimestamp = localProfile.lastModified
      ? new Date(localProfile.lastModified).getTime()
      : 0;
    const remoteTimestamp = remoteProfile?.updated_at
      ? new Date(remoteProfile.updated_at).getTime()
      : 0;

    if (localTimestamp >= remoteTimestamp) {
      // Local is newer, sync to remote (remote uses snake_case)
      await updateUserProfile(userId, {
        total_xp: localProfile.totalXp,
        current_level: localProfile.currentLevel,
        lifetime_xp: localProfile.lifetimeXp,
        current_streak: localProfile.currentStreak,
        longest_streak: localProfile.longestStreak,
        last_active_date: localProfile.lastActiveDate,
        membership_tier: localProfile.membershipTier,
        freezes_available: localProfile.freezesAvailable,
        freezes_used: localProfile.freezesUsed,
        books_completed: localProfile.booksCompleted,
        streak_multiplier: localProfile.streakMultiplier,
        challenges_completed: localProfile.challengesCompleted,
        daily_challenge_streak: localProfile.dailyChallengeStreak,
      });
    } else if (remoteProfile) {
      // Remote is newer, sync to local (local uses camelCase)
      await saveLocalUserProfile({
        ...localProfile,
        totalXp: remoteProfile.total_xp,
        currentLevel: remoteProfile.current_level,
        lifetimeXp: remoteProfile.lifetime_xp,
        currentStreak: remoteProfile.current_streak,
        longestStreak: remoteProfile.longest_streak,
        lastActiveDate: remoteProfile.last_active_date || '',
        membershipTier: remoteProfile.membership_tier,
        freezesAvailable: remoteProfile.freezes_available,
        freezesUsed: remoteProfile.freezes_used,
        booksCompleted: remoteProfile.books_completed,
        streakMultiplier: remoteProfile.streak_multiplier,
        challengesCompleted: remoteProfile.challenges_completed,
        dailyChallengeStreak: remoteProfile.daily_challenge_streak,
        lastModified: remoteProfile.updated_at,
      });
    }

    return true;
  } catch (error) {
    console.error('Error syncing user profile:', error);
    return false;
  }
}

/**
 * Sync local achievements to Supabase (Ultra only)
 */
async function syncAchievements(userId: string, userTier: MembershipTier): Promise<boolean> {
  if (userTier !== 'ultra') return false;

  try {
    // Get local achievements (needs userId)
    const localAchievements = await getUserAchievementsLocal(userId);
    
    // Get remote achievements
    const remoteAchievements = await getUserAchievements(userId);

    // Sync local to remote (new achievements)
    for (const localAch of localAchievements) {
      const existsRemote = remoteAchievements.some(
        (ra: { achievement_id: string }) => ra.achievement_id === localAch.achievementId
      );
       
      if (!existsRemote) {
        await unlockAchievement(userId, localAch.achievementId, localAch.xpEarned);
      }
    }

    // Sync remote to local (achievements unlocked elsewhere)
    for (const remoteAch of remoteAchievements) {
      const existsLocal = localAchievements.some(
        (la: { achievementId: string }) => la.achievementId === remoteAch.achievement_id
      );
       
      if (!existsLocal) {
        await saveUserAchievementLocal({
          userId,
          achievementId: remoteAch.achievement_id,
          unlockedAt: remoteAch.unlocked_at,
          xpEarned: remoteAch.xp_earned,
        });
      }
    }

    return true;
  } catch (error) {
    console.error('Error syncing achievements:', error);
    return false;
  }
}

/**
 * Sync reading sessions to Supabase (Ultra only)
 */
async function syncReadingSessions(userId: string, userTier: MembershipTier): Promise<boolean> {
  if (userTier !== 'ultra') return false;
  try {
    const sessions = await getUnsyncedReadingSessions(userId);
    if (sessions.length === 0) return false;

    let syncedAny = false;
    for (const session of sessions) {
      const { data, error } = await supabase
        .from('user_reading_sessions')
        .insert({
          user_id: userId,
          words_read: session.wordsRead,
          duration_seconds: session.durationSeconds,
          avg_wpm: session.avgWpm,
          max_wpm: session.maxWpm,
          session_date: session.sessionDate,
          created_at: session.createdAt,
        })
        .select('id')
        .single();

      if (!error && data) {
        await markReadingSessionSynced(session.id!, data.id);
        syncedAny = true;
      } else {
        console.error('Failed to sync reading session:', error);
      }
    }
    return syncedAny;
  } catch (error) {
    console.error('Error syncing reading sessions:', error);
    return false;
  }
}

/**
 * Sync daily challenges to Supabase (Ultra only)
 */
async function syncDailyChallenges(userId: string, userTier: MembershipTier): Promise<boolean> {
  if (userTier !== 'ultra') return false;
  try {
    const challenges = await getUnsyncedDailyChallenges(userId);
    if (challenges.length === 0) return false;

    let syncedAny = false;
    for (const ch of challenges) {
      const { data, error } = await supabase
        .from('user_daily_challenges')
        .insert({
          user_id: userId,
          challenge_date: ch.challengeDate,
          challenge_id: ch.challengeId,
          completed: ch.completed,
          completed_at: ch.completedAt,
          xp_earned: ch.xpEarned,
        })
        .select('id')
        .single();

      if (!error && data) {
        await markDailyChallengeSynced(ch.id!, data.id);
        syncedAny = true;
      } else {
        console.error('Failed to sync daily challenge:', error);
      }
    }
    return syncedAny;
  } catch (error) {
    console.error('Error syncing daily challenges:', error);
    return false;
  }
}

/**
 * Sync book progress and chapter progress to Supabase (Ultra only)
 */
async function syncBookProgress(userId: string, userTier: MembershipTier): Promise<boolean> {
  if (userTier !== 'ultra') return false;
  try {
    const allLocalProgress = await getAllProgress();
    if (allLocalProgress.length === 0) return false;

    let syncedAny = false;

    for (const local of allLocalProgress) {
      // Prepare book progress data
      const currentChapterIndex = parseInt(local.currentChapterId?.replace(/\D/g, '') || '0', 10);
      const progressPercentage = local.totalWords
        ? (local.currentWordIndex / local.totalWords) * 100
        : 0;

      const { data: bookRecord, error: bookError } = await supabase
        .from('user_book_progress')
        .upsert(
          {
            user_id: userId,
            book_id: local.bookId,
            book_title: local.bookTitle,
            current_chapter: currentChapterIndex,
            current_position: local.currentWordIndex,
            progress_percentage: progressPercentage,
            last_read_at: local.lastReadAt,
          },
          { onConflict: 'user_id, book_id' }
        )
        .select('id')
        .single();

      if (bookError || !bookRecord) {
        console.error('Failed to upsert book progress for', local.bookId, bookError);
        continue;
      }

      const bookProgressId = bookRecord.id;
      syncedAny = true;

      // Sync chapter progress
      for (const chapter of local.chapterProgress) {
        const chapterIndex = parseInt(chapter.chapterId.replace(/\D/g, ''), 10);
        await supabase
          .from('user_chapter_progress')
          .upsert(
            {
              user_book_progress_id: bookProgressId,
              chapter_index: chapterIndex,
              position: chapter.wordIndex,
              completed: chapter.completed,
            },
            { onConflict: 'user_book_progress_id, chapter_index' }
          )
          .select()
          .single();
      }
    }

    return syncedAny;
  } catch (error) {
    console.error('Error syncing book progress:', error);
    return false;
  }
}

/**
 * Main sync function - syncs all data for Ultra users
 * Non-blocking, fire-and-forget
 */
export async function syncToCloud(
  userId: string,
  userTier: MembershipTier
): Promise<SyncStatus> {
  if (!await canSync(userTier)) {
    return 'synced'; // No sync needed for non-Ultra
  }

  try {
    // Sync profile first
    const profileSynced = await syncUserProfile(userId, userTier);
    
    // Sync achievements
    const achievementsSynced = await syncAchievements(userId, userTier);

    // Sync reading sessions
    const sessionsSynced = await syncReadingSessions(userId, userTier);

    // Sync daily challenges
    const challengesSynced = await syncDailyChallenges(userId, userTier);

    // Sync book and chapter progress
    const bookProgressSynced = await syncBookProgress(userId, userTier);

    if (profileSynced || achievementsSynced || sessionsSynced || challengesSynced || bookProgressSynced) {
      updateLastSyncTime();
      return 'synced';
    }

    return 'pending';
  } catch (error) {
    console.error('Sync failed:', error);
    return 'error';
  }
}

/**
 * Manual sync trigger - use this for on-demand sync (logout, manual button)
 */
export async function manualSync(
  userId: string,
  userTier: MembershipTier
): Promise<SyncStatus> {
  return syncToCloud(userId, userTier);
}

/**
 * Get current sync status (Ultra users only)
 * Returns 'pending' if never synced, otherwise 'synced'
 */
export function getSyncStatus(userTier: MembershipTier): SyncStatus {
  if (userTier !== 'ultra') return 'synced';
  
  const lastSync = getLastSyncTime();
  if (!lastSync) return 'pending';
  
  return 'synced';
}
